import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { getCurrentHistoryIndex, isCurrentHistoryEntryFreshPush } from "./location-store";

// Scroll positions keyed by history index, module-level so they outlive the
// remount of the scroll container across route changes. A browser normally
// restores scroll on Back/Forward, but once the page's scroll moves into a
// bounded, remounting element (the shell's `<main>`) the browser no longer owns
// that position — so this restores it per history entry.
const positions = new Map<number, { resetKey: string | undefined; top: number }>();

export function deriveMainScrollResetKey(pathname: string): string {
  if (pathname === "/approvals" || pathname.startsWith("/approvals/")) return "/approvals";
  const specialistRecord = pathname.match(/^\/specialists\/([^/]+)/)?.[1];
  return specialistRecord ? `/specialists/${specialistRecord}` : pathname;
}

/**
 * Restore the shell's main-scroll position per history entry. While `enabled`
 * (a page-class route, whose content scrolls inside `<main>`), each scroll is
 * recorded against the current history index. Back/Forward restores the saved
 * offset. A fresh entry retains the current offset unless the optional
 * `resetKey` changes; callers opt into reset semantics by supplying that key.
 * Disabled for workbench routes, whose panes own their own scroll.
 *
 * The saved position keys on the history index so two visits to the same route
 * retain independent Back/Forward positions. The reset key has a different
 * job: it identifies the record whose subroutes share one working position.
 */
export function useMainScrollRestoration(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  resetKey?: string,
): void {
  const historyIndex = getCurrentHistoryIndex();
  const freshPush = isCurrentHistoryEntryFreshPush();
  const currentResetKey = useRef(resetKey);
  const previousResetKey = useRef(resetKey);
  currentResetKey.current = resetKey;

  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;
    const onScroll = () => {
      positions.set(getCurrentHistoryIndex(), {
        resetKey: currentResetKey.current,
        top: element.scrollTop,
      });
    };
    element.addEventListener("scroll", onScroll, { passive: true });
    return () => element.removeEventListener("scroll", onScroll);
  }, [enabled, ref]);

  // Layout effect so the restore lands before paint — no visible jump from 0 to
  // the saved offset.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || !element) {
      previousResetKey.current = resetKey;
      return;
    }
    const saved = positions.get(historyIndex);
    if (!freshPush && saved && saved.resetKey === resetKey) {
      element.scrollTop = saved.top;
    } else if (resetKey !== undefined && previousResetKey.current !== resetKey) {
      element.scrollTop = 0;
    }
    positions.set(historyIndex, { resetKey, top: element.scrollTop });
    previousResetKey.current = resetKey;
  }, [historyIndex, freshPush, enabled, ref, resetKey]);
}
